import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Save, Eye, ArrowLeft, Plus, Trash2, CheckCircle, Trash } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { prestataireService } from '../services/api';
import { useAuthStore } from '../store/authStore';

const SERVICES_OPTIONS = [
  { key: 'MECANIQUE_MOTO',     label: '🏍️ Mécanique Moto' },
  { key: 'MECANIQUE_VOITURE',  label: '🚗 Mécanique Voiture' },
  { key: 'VULCANISATEUR',      label: '🔩 Vulcanisateur' },
  { key: 'ELECTRICITE_AUTO',   label: '⚡ Électricité Auto' },
  { key: 'CARROSSERIE',        label: '🎨 Carrosserie' },
  { key: 'CLIMATISATION',      label: '❄️ Climatisation' },
  { key: 'VIDANGE_ENTRETIEN',  label: '🔧 Vidange & Entretien' },
  { key: 'PIECES_DETACHEES',   label: '⚙️ Pièces détachées' },
];

const PAYMENT_OPTIONS = [
  { key: 'MOBILE_MONEY',  label: '📱 Mobile Money (MTN)' },
  { key: 'MOOV_MONEY',    label: '🟦 Moov Money' },
  { key: 'CELTIS_CASH',   label: '🟠 Celtis Cash' },
];

const CITIES = ['Cotonou', 'Porto-Novo'];

const TABS = [
  { key: 'infos',    label: 'Infos générales', icon: '👤' },
  { key: 'services', label: 'Services & Tarifs', icon: '🔧' },
  { key: 'photos',   label: 'Photos',           icon: '📷' },
  { key: 'security', label: 'Sécurité',         icon: '🔒' },
];

const Input = ({ label, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
    <label style={{ fontSize: '0.78rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1.5px', color: 'var(--gray-text)', textTransform: 'uppercase' }}>{label}</label>
    <input {...props} style={{ background: 'white', border: '1.5px solid var(--gray-mid)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.92rem', color: 'var(--blue-dark)', outline: 'none', transition: 'border-color 0.2s', ...props.style }}
      onFocus={e => e.target.style.borderColor = 'var(--orange)'}
      onBlur={e => e.target.style.borderColor = 'var(--gray-mid)'} />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
    <label style={{ fontSize: '0.78rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1.5px', color: 'var(--gray-text)', textTransform: 'uppercase' }}>{label}</label>
    <textarea {...props} style={{ background: 'white', border: '1.5px solid var(--gray-mid)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.92rem', color: 'var(--blue-dark)', outline: 'none', resize: 'vertical', minHeight: '100px', fontFamily: 'inherit', ...props.style }}
      onFocus={e => e.target.style.borderColor = 'var(--orange)'}
      onBlur={e => e.target.style.borderColor = 'var(--gray-mid)'} />
  </div>
);

export default function EditProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('infos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [profile, setProfile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [realisationUploading, setRealisationUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const profilePhotoRef = useRef();
  const realisationRef = useRef();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      const p = r.data.prestataire;
      setProfile({
        firstName:       p.firstName       || '',
        lastName:        p.lastName        || '',
        shopName:        p.shopName        || '',
        bio:             p.bio             || '',
        city:            p.city            || '',
        neighborhood:    p.neighborhood    || '',
        address:         p.address         || '',
        experienceYears: p.experienceYears || 0,
        availability:    p.availability    || 'Lun-Sam 7h-18h',
        priceMin:        p.priceMin        || '',
        priceMax:        p.priceMax        || '',
        whatsapp:        p.whatsapp        || '',
        secondPhone:     p.secondPhone     || '',
        momoPhone:       p.momoPhone       || '',
        momoNetwork:     p.momoNetwork     || 'MTN',
        cip:             p.cip             || '',
        ifu:             p.ifu             || '',
        urgenceNom:      p.urgenceNom      || '',
        urgencePhone:    p.urgencePhone    || '',
        isAvailable:     p.isAvailable     ?? true,
        profilePhotoUrl: p.profilePhotoUrl || '',
        services:        p.services?.map(s => s.service)       || [],
        paymentMethods:  p.paymentMethods?.map(m => m.method)  || [],
        photos:          p.photos          || [],
      });
    }).catch(() => {
      toast.error('Impossible de charger le profil.');
    }).finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setProfile(prev => ({ ...prev, [key]: val }));

  const toggleService = (key) => setProfile(prev => ({
    ...prev,
    services: prev.services.includes(key)
      ? prev.services.filter(s => s !== key)
      : [...prev.services, key],
  }));

  const togglePayment = (key) => setProfile(prev => ({
    ...prev,
    paymentMethods: prev.paymentMethods.includes(key)
      ? prev.paymentMethods.filter(m => m !== key)
      : [...prev.paymentMethods, key],
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await prestataireService.updateProfile({
        firstName: profile.firstName, lastName: profile.lastName,
        shopName: profile.shopName, bio: profile.bio,
        city: profile.city, neighborhood: profile.neighborhood, address: profile.address,
        experienceYears: parseInt(profile.experienceYears) || 0,
        availability: profile.availability,
        priceMin: profile.priceMin ? parseInt(profile.priceMin) : null,
        priceMax: profile.priceMax ? parseInt(profile.priceMax) : null,
        whatsapp: profile.whatsapp, secondPhone: profile.secondPhone,
        momoPhone: profile.momoPhone, momoNetwork: profile.momoNetwork,
        cip: profile.cip, ifu: profile.ifu,
        urgenceNom: profile.urgenceNom, urgencePhone: profile.urgencePhone,
        isAvailable: profile.isAvailable,
        services: profile.services, paymentMethods: profile.paymentMethods,
      });
      updateUser({ firstName: profile.firstName, lastName: profile.lastName });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast.success('Profil mis à jour !');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la sauvegarde.');
    } finally { setSaving(false); }
  };

  // ── Changer la photo de profil ──
  const handleProfilePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const { data } = await prestataireService.uploadProfilePhoto(file);
      set('profilePhotoUrl', data.profilePhotoUrl);
      updateUser({ profilePhotoUrl: data.profilePhotoUrl });
      toast.success('Photo de profil mise à jour !');
    } catch { toast.error('Erreur upload photo.'); }
    finally { setPhotoUploading(false); }
  };

  // ── SUPPRIMER la photo de profil ──
  const handleDeleteProfilePhoto = async () => {
    setShowDeleteConfirm(false);
    setDeletingPhoto(true);
    try {
      await api.delete('/prestataires/photo/profile');
      set('profilePhotoUrl', '');
      updateUser({ profilePhotoUrl: null });
      toast.success('Photo de profil supprimée.');
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally { setDeletingPhoto(false); }
  };

  const handleAddRealisation = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRealisationUploading(true);
    try {
      const { data } = await prestataireService.addPhoto(file, caption);
      set('photos', [...profile.photos, data]);
      setCaption('');
      toast.success('Photo ajoutée !');
    } catch { toast.error('Erreur upload photo.'); }
    finally { setRealisationUploading(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await prestataireService.deletePhoto(photoId);
      set('photos', profile.photos.filter(p => p.id !== photoId));
      toast.success('Photo supprimée.');
    } catch { toast.error('Erreur suppression.'); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error('Les mots de passe ne correspondent pas.'); return; }
    if (newPwd.length < 8) { toast.error('Minimum 8 caractères.'); return; }
    setPwdSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword: currentPwd, newPassword: newPwd });
      toast.success('Mot de passe mis à jour !');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Mot de passe actuel incorrect.');
    } finally { setPwdSaving(false); }
  };

  if (loading || !profile) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '80px' }}>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ fontSize: '3rem' }}>⚙️</motion.div>
    </div>
  );

  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>

      {/* ── MODAL CONFIRMATION SUPPRESSION PHOTO ── */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '360px', width: '100%', textAlign: 'center', boxShadow: '0 30px 80px rgba(0,0,0,0.3)' }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '0.8rem' }}>🗑️</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: 'var(--blue-dark)', marginBottom: '0.5rem', letterSpacing: '1px' }}>
                SUPPRIMER LA PHOTO ?
              </h3>
              <p style={{ color: 'var(--gray-text)', fontSize: '0.88rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                Votre photo de profil sera supprimée et remplacée par vos initiales.
              </p>
              <div style={{ display: 'flex', gap: '0.8rem' }}>
                <button onClick={() => setShowDeleteConfirm(false)}
                  style={{ flex: 1, padding: '0.75rem', borderRadius: '10px', border: '1.5px solid var(--gray-mid)', background: 'white', color: 'var(--blue-dark)', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', letterSpacing: '1px' }}>
                  ANNULER
                </button>
                <button onClick={handleDeleteProfilePhoto}
                  style={{ flex: 1, padding: '0.75rem', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #EF4444, #B91C1C)', color: 'white', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', letterSpacing: '1px', boxShadow: '0 4px 15px rgba(239,68,68,0.4)' }}>
                  SUPPRIMER
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg, var(--blue-dark), #0D1B2A)', padding: '2.5rem 0 0' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>

            {/* ── PHOTO DE PROFIL + actions ── */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ position: 'relative' }}>
                {/* Photo ou initiales — cliquable pour changer */}
                <div onClick={() => profilePhotoRef.current.click()} style={{ cursor: 'pointer' }}>
                  {profile.profilePhotoUrl ? (
                    <img src={profile.profilePhotoUrl} alt="Photo de profil"
                      style={{ width: 90, height: 90, borderRadius: '50%', objectFit: 'cover', border: '4px solid var(--orange)', boxShadow: '0 8px 25px rgba(255,107,0,0.4)', display: 'block' }} />
                  ) : (
                    <div style={{ width: 90, height: 90, borderRadius: '50%', background: 'linear-gradient(135deg, var(--orange), #CC5500)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontFamily: 'var(--font-display)', fontSize: '2rem', border: '4px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 25px rgba(255,107,0,0.4)' }}>
                      {initials}
                    </div>
                  )}
                  {/* Overlay au survol */}
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                  >
                    <Camera size={22} color="white" />
                  </div>
                </div>

                {/* Bouton caméra */}
                <button onClick={() => profilePhotoRef.current.click()}
                  style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--orange)', border: '2px solid var(--blue-dark)', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  <Camera size={13} />
                </button>

                {/* Spinner upload */}
                {(photoUploading || deletingPhoto) && (
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} style={{ color: 'white', fontSize: '1.2rem' }}>⚙</motion.div>
                  </div>
                )}
              </div>

              <input ref={profilePhotoRef} type="file" accept="image/*" onChange={handleProfilePhoto} style={{ display: 'none' }} />

              {/* Boutons sous la photo */}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button onClick={() => profilePhotoRef.current.click()}
                  style={{ fontSize: '0.7rem', fontFamily: 'var(--font-cond)', fontWeight: 700, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '0.3rem 0.7rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Camera size={11} /> Changer
                </button>
                {/* ✅ BOUTON SUPPRIMER LA PHOTO — visible seulement si photo présente */}
                {profile.profilePhotoUrl && (
                  <button onClick={() => setShowDeleteConfirm(true)} disabled={deletingPhoto}
                    style={{ fontSize: '0.7rem', fontFamily: 'var(--font-cond)', fontWeight: 700, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#FCA5A5', padding: '0.3rem 0.7rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Trash size={11} /> Supprimer
                  </button>
                )}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', color: 'white', letterSpacing: '1px', lineHeight: 1, marginBottom: '0.3rem' }}>
                {profile.firstName?.toUpperCase()} {profile.lastName?.toUpperCase()}
              </h1>
              {profile.shopName && <p style={{ color: 'var(--orange)', fontFamily: 'var(--font-cond)', fontWeight: 700 }}>{profile.shopName}</p>}
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Gérez votre profil prestataire</p>
            </div>

            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/espace-prestataire')}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', padding: '0.6rem 1.2rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem' }}>
                <ArrowLeft size={16} /> Tableau de bord
              </button>
              <button onClick={() => navigate(`/prestataires/${user?.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', padding: '0.6rem 1.2rem', borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem' }}>
                <Eye size={16} /> Voir mon profil
              </button>
            </div>
          </div>

          {/* TABS */}
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ background: activeTab === tab.key ? 'rgba(255,107,0,0.15)' : 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #FF6B00' : '3px solid transparent', color: activeTab === tab.key ? 'white' : 'rgba(255,255,255,0.5)', padding: '0.9rem 1.5rem', cursor: 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '1.5px', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="container" style={{ padding: '2.5rem 1.5rem 5rem', maxWidth: '820px' }}>
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>

          {/* INFOS */}
          {activeTab === 'infos' && (
            <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <h2 style={sectionH}>Informations personnelles</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Input label="Prénom" value={profile.firstName} onChange={e => set('firstName', e.target.value)} />
                <Input label="Nom" value={profile.lastName} onChange={e => set('lastName', e.target.value)} />
              </div>
              <Input label="Nom de la boutique (optionnel)" value={profile.shopName} onChange={e => set('shopName', e.target.value)} />
              <Textarea label="Biographie / Présentation" value={profile.bio} onChange={e => set('bio', e.target.value)} placeholder="Parlez de votre expérience..." />

              <h2 style={sectionH}>Localisation</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label style={{ fontSize: '0.78rem', fontFamily: 'var(--font-cond)', fontWeight: 700, letterSpacing: '1.5px', color: 'var(--gray-text)', textTransform: 'uppercase' }}>Ville</label>
                  <select value={profile.city} onChange={e => set('city', e.target.value)}
                    style={{ background: 'white', border: '1.5px solid var(--gray-mid)', borderRadius: '10px', padding: '0.75rem 1rem', fontSize: '0.92rem', color: 'var(--blue-dark)', outline: 'none' }}>
                    {CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <Input label="Quartier" value={profile.neighborhood} onChange={e => set('neighborhood', e.target.value)} />
              </div>
              <Input label="Adresse complète (optionnel)" value={profile.address} onChange={e => set('address', e.target.value)} />

              <h2 style={sectionH}>Disponibilité & Contact</h2>
              <Input label="Horaires" value={profile.availability} onChange={e => set('availability', e.target.value)} />
              <Input label="Numéro WhatsApp" value={profile.whatsapp} onChange={e => set('whatsapp', e.target.value)} />
              <Input label="Deuxième téléphone (optionnel)" value={profile.secondPhone} onChange={e => set('secondPhone', e.target.value)} />

              <h2 style={sectionH}>💳 Réception des paiements</h2>
              <p style={{ fontSize: '0.83rem', color: 'var(--gray-text)', marginBottom: '0.8rem' }}>
                Numéro sur lequel vous recevrez vos paiements automatiquement.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Input label="Numéro MoMo *" value={profile.momoPhone} onChange={e => set('momoPhone', e.target.value)} placeholder="+229 XX XX XX XX" />
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--blue-dark)', display: 'block', marginBottom: '0.4rem' }}>Réseau *</label>
                  <select className="form-select" value={profile.momoNetwork} onChange={e => set('momoNetwork', e.target.value)}>
                    <option value="MTN">MTN Mobile Money</option>
                    <option value="MOOV">Moov Money</option>
                    <option value="CELTIIS">Celtiis Cash</option>
                  </select>
                </div>
              </div>

              <h2 style={sectionH}>📋 Documents officiels</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Input label="Numéro CIP *" value={profile.cip} onChange={e => set('cip', e.target.value)} placeholder="Ex: BJ-XXXXXX" />
                <Input label="Numéro IFU (optionnel)" value={profile.ifu} onChange={e => set('ifu', e.target.value)} placeholder="Ex: 123456789" />
              </div>

              <h2 style={sectionH}>🆘 Contact d'urgence</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <Input label="Nom & Prénom *" value={profile.urgenceNom} onChange={e => set('urgenceNom', e.target.value)} placeholder="Ex: Marie Koffi" />
                <Input label="Téléphone *" value={profile.urgencePhone} onChange={e => set('urgencePhone', e.target.value)} placeholder="+229 XX XX XX XX" />
              </div>


              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.2rem', background: profile.isAvailable ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: '12px', border: `1.5px solid ${profile.isAvailable ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-cond)', fontWeight: 700, color: 'var(--blue-dark)' }}>Disponibilité</div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--gray-text)' }}>Les clients voient si vous êtes disponible</div>
                </div>
                <div onClick={() => set('isAvailable', !profile.isAvailable)}
                  style={{ width: '52px', height: '28px', borderRadius: '14px', background: profile.isAvailable ? '#10B981' : '#D1D5DB', cursor: 'pointer', position: 'relative', transition: 'background 0.3s' }}>
                  <div style={{ position: 'absolute', top: '3px', left: profile.isAvailable ? '26px' : '3px', width: '22px', height: '22px', borderRadius: '50%', background: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', transition: 'left 0.3s' }} />
                </div>
              </div>
            </div>
          )}

          {/* SERVICES */}
          {activeTab === 'services' && (
            <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <div>
                <h2 style={sectionH}>Mes spécialités</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.8rem' }}>
                  {SERVICES_OPTIONS.map(s => (
                    <div key={s.key} onClick={() => toggleService(s.key)}
                      style={{ padding: '0.9rem 1rem', borderRadius: '12px', cursor: 'pointer', border: `2px solid ${profile.services.includes(s.key) ? 'var(--orange)' : 'var(--gray-mid)'}`, background: profile.services.includes(s.key) ? 'rgba(255,107,0,0.06)' : 'white', display: 'flex', alignItems: 'center', gap: '0.6rem', transition: 'all 0.2s' }}>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: `2px solid ${profile.services.includes(s.key) ? 'var(--orange)' : 'var(--gray-mid)'}`, background: profile.services.includes(s.key) ? 'var(--orange)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {profile.services.includes(s.key) && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />}
                      </div>
                      <span style={{ fontSize: '0.88rem', fontWeight: 600, color: profile.services.includes(s.key) ? 'var(--orange)' : 'var(--blue-dark)' }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 style={sectionH}>Tarifs</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <Input label="Tarif minimum (FCFA)" type="number" value={profile.priceMin} onChange={e => set('priceMin', e.target.value)} />
                  <Input label="Tarif maximum (FCFA)" type="number" value={profile.priceMax} onChange={e => set('priceMax', e.target.value)} />
                </div>
                <Input label="Années d'expérience" type="number" value={profile.experienceYears} onChange={e => set('experienceYears', e.target.value)} style={{ marginTop: '1rem' }} />
              </div>
              <div>
                <h2 style={sectionH}>Moyens de paiement acceptés</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                  {PAYMENT_OPTIONS.map(p => (
                    <div key={p.key} onClick={() => togglePayment(p.key)}
                      style={{ padding: '0.9rem 1.2rem', borderRadius: '12px', cursor: 'pointer', border: `2px solid ${profile.paymentMethods.includes(p.key) ? 'var(--orange)' : 'var(--gray-mid)'}`, background: profile.paymentMethods.includes(p.key) ? 'rgba(255,107,0,0.06)' : 'white', display: 'flex', alignItems: 'center', gap: '0.8rem', transition: 'all 0.2s' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${profile.paymentMethods.includes(p.key) ? 'var(--orange)' : 'var(--gray-mid)'}`, background: profile.paymentMethods.includes(p.key) ? 'var(--orange)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {profile.paymentMethods.includes(p.key) && <CheckCircle size={14} color="white" />}
                      </div>
                      <span style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--blue-dark)' }}>{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PHOTOS */}
          {activeTab === 'photos' && (
            <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-sm)' }}>
              <h2 style={sectionH}>Photos de réalisations</h2>
              <p style={{ color: 'var(--gray-text)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>Montrez vos travaux pour attirer plus de clients.</p>
              <div style={{ border: '2px dashed var(--gray-mid)', borderRadius: '14px', padding: '1.5rem', marginBottom: '1.5rem', background: 'var(--gray-light)' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <Input label="Légende (optionnel)" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Ex: Changement d'embrayage Peugeot 206" />
                  </div>
                  <button onClick={() => realisationRef.current.click()} disabled={realisationUploading}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: realisationUploading ? 'var(--gray-mid)' : 'var(--blue-dark)', border: 'none', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '10px', cursor: realisationUploading ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                    {realisationUploading ? '⏳ Upload...' : <><Plus size={16} /> Ajouter une photo</>}
                  </button>
                  <input ref={realisationRef} type="file" accept="image/*" onChange={handleAddRealisation} style={{ display: 'none' }} />
                </div>
              </div>
              {profile.photos.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-text)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📷</div>
                  <p>Aucune photo pour l'instant. Ajoutez vos réalisations !</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.8rem' }}>
                  {profile.photos.map(photo => (
                    <div key={photo.id} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: '10px', overflow: 'hidden' }}>
                      <img src={photo.photoUrl} alt={photo.caption || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      {photo.caption && (
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '0.5rem', fontSize: '0.7rem', color: 'white' }}>{photo.caption}</div>
                      )}
                      <button onClick={() => handleDeletePhoto(photo.id)}
                        style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(239,68,68,0.9)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SECURITE */}
          {activeTab === 'security' && (
            <div style={{ background: 'white', borderRadius: 16, padding: '2rem', boxShadow: 'var(--shadow-sm)' }}>
              <h2 style={sectionH}>Changer le mot de passe</h2>
              <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', maxWidth: '400px' }}>
                <Input label="Mot de passe actuel" type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} required />
                <Input label="Nouveau mot de passe" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required />
                <Input label="Confirmer le nouveau mot de passe" type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required />
                <button type="submit" disabled={pwdSaving} className="btn btn-primary">
                  {pwdSaving ? 'Mise à jour...' : '🔒 Mettre à jour le mot de passe'}
                </button>
              </form>
            </div>
          )}

          {/* BOUTON SAUVEGARDER */}
          {activeTab !== 'security' && activeTab !== 'photos' && (
            <motion.button onClick={handleSave} disabled={saving} whileTap={{ scale: 0.97 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', width: '100%', marginTop: '1.5rem', background: saved ? '#10B981' : 'linear-gradient(135deg, #FF6B00, #CC4400)', border: 'none', color: 'white', padding: '1rem', borderRadius: '12px', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '1rem', letterSpacing: '1.5px', boxShadow: '0 8px 25px rgba(255,107,0,0.3)', opacity: saving ? 0.7 : 1, transition: 'background 0.3s' }}>
              {saved ? <><CheckCircle size={20} /> SAUVEGARDÉ !</> : saving ? '⏳ Sauvegarde...' : <><Save size={20} /> SAUVEGARDER LES MODIFICATIONS</>}
            </motion.button>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const sectionH = { fontFamily: 'var(--font-cond)', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: 'var(--orange)', borderBottom: '2px solid rgba(255,107,0,0.15)', paddingBottom: '0.6rem', marginBottom: '1.2rem' };
